<script lang="ts" setup>
import { getUniqueId } from "@/shared/helper/uniqueId";
import InputGroup from "../InputGroup/InputGroup.vue";

withDefaults(
  defineProps<{
    modelValue?: string;
    options: Array<{ key: string; value: string; disabled?: boolean }>;
    label: string;
    errors?: Array<string>;
    isErrored?: boolean;
    disabled?: boolean;
    placeholderText?: string;
    required?: boolean;
    isLabelHidden?: boolean;
    withTitleAttribute?: boolean;
    hint?: string;
    resettable?: boolean;
  }>(),
  {
    errors: () => [],
  },
);
const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const onChange = (event: Event) => {
  emit("update:modelValue", (event.target as HTMLSelectElement).value);
};

const inputId = getUniqueId("select-field");
</script>

<template>
  <InputGroup
    :id="inputId"
    :label="label"
    :error-messages="errors"
    :is-errored="isErrored"
    :is-disabled="disabled"
    :is-required="required"
    :is-label-hidden="isLabelHidden"
    :hint="hint"
    hide-error-append-icon
  >
    <template #default="{ inputBindings }">
      <select
        :id="inputId"
        class="select-field"
        :value="modelValue"
        :disabled="disabled"
        :required="required"
        v-bind="inputBindings"
        :data-no-value="!modelValue"
        @change="onChange"
      >
        <!-- The button element is required to make the select customizable (see docs: https://developer.mozilla.org/en-US/docs/Learn_web_development/Extensions/Forms/Customizable_select) -->
        <button>
          <selectedcontent></selectedcontent>
        </button>
        <option
          class="select-field--option"
          :disabled="!resettable || !modelValue"
          value
          :selected="!modelValue"
        >
          <span class="select-field--option-text">
            {{ placeholderText || $t("shared.form.field.select.noValue") }}
          </span>
        </option>
        <option
          v-for="option in options"
          :key="option.key"
          class="select-field--option"
          :value="option.key"
          :disabled="option.disabled"
          :title="withTitleAttribute ? option.value : undefined"
        >
          <span class="select-field--option-text">{{ option.value }}</span>
        </option>
      </select>
    </template>
  </InputGroup>
</template>

<style scoped>
@import "SelectField.css";
</style>
